import { useState } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import DialogContainer from "../../../../../../components/dialog-container/dialog-container";
import TextInput from "../../../../../../components/textinput/textinput";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import * as yup from "yup";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import {
  addCoverageAreaRequest,
  updateCoverageAreaRequest,
  deleteCoverageAreaRequest,
} from "../../../../../../utils/APICalls";
import Loading from "../../../../../../components/loading/loading";
import { getCoverageAreasRequest } from "../../../../../../redux/inspection-firms/slice";

type Props = {
  showDialog: boolean;
  closeDialog: () => void;
  data?: any;
  addCoverageAreaCase?: boolean;
};

interface FormValues {
  coverageAreaName: string;
  country: string;
  zipCodes: string;
}

const COUNTRIES = [
  { code: "USA", name: "USA" },
  { code: "Canada", name: "Canada" },
];

const validationSchema = yup.object({
  coverageAreaName: yup
    .string()
    .required("Coverage Area Name is required")
    .min(2, "Coverage Area Name must be at least 2 characters")
    .max(100, "Coverage Area Name must be less than 100 characters"),
  country: yup.string().required("Country is required"),
  zipCodes: yup
    .string()
    .required("Zip Codes are required")
    .test("valid-zip-codes", "Please enter valid zip codes", function (value) {
      if (!value) return false;

      const country = this.parent.country;

      const zipCodes = value
        .split(/[\n,]/)
        .map((zip) => zip.trim())
        .filter((zip) => zip.length > 0);

      if (zipCodes.length === 0) return false;

      if (country === "USA") {
        // USA: Only 5-digit numbers allowed
        return zipCodes.every((zip) => /^\d{5}$/.test(zip));
      } else if (country === "Canada") {
        // Canada: Format: Letter-Number-Letter Space Number-Letter-Number
        return zipCodes.every((zip) => {
          const cleanZip = zip.toUpperCase().replace(/\s+/g, " ").trim();
          return /^[A-Z]\d[A-Z]\s?\d[A-Z]\d$/.test(cleanZip);
        });
      } else {
        // Other countries: Basic alphanumeric validation
        return zipCodes.every((zip) => /^[A-Z0-9]{3,10}$/.test(zip));
      }
    }),
});

export default function EditCoverageArea({
  showDialog,
  closeDialog,
  data,
  addCoverageAreaCase = false,
}: Props) {
  const dispatch = useDispatch();
  const [showLoading, setShowLoading] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const formik = useFormik<FormValues>({
    initialValues: {
      coverageAreaName: data?.displayName || "",
      country: data?.country || "",
      zipCodes: data?.zipCodes
        ? data.zipCodes.map((zip: any) => zip.code).join("\n")
        : "",
    },
    validationSchema,
    onSubmit: async (values) => {
      const isValid = await formik.validateForm();
      if (!isValid) {
        formik.setTouched({
          coverageAreaName: true,
          country: true,
          zipCodes: true,
        });
        return;
      }

      updateCoverageAreaDetails(values);
    },
    enableReinitialize: true,
  });

  const updateCoverageAreaDetails = async (values: any) => {
    const zipCodesArray = values.zipCodes
      .split(/[\n,]/)
      .map((zip: string) => zip.trim())
      .filter((zip: string) => zip.length > 0);

    const payload = {
      displayName: values.coverageAreaName,
      country: values.country,
      zipCodes: zipCodesArray,
    };

    setShowLoading(true);

    try {
      let response;
      if (addCoverageAreaCase) {
        response = await addCoverageAreaRequest(
          data?.inspectionFirmId!,
          payload
        );
      } else {
        const updatePayload = {
          ...payload,
          id: data?.id,
        };
        response = await updateCoverageAreaRequest(
          data?.inspectionFirmId!,
          updatePayload
        );
      }

      setShowLoading(false);

      if (
        response?.status === 200 ||
        response?.status === 201 ||
        response?.status === 204
      ) {
        dispatch(getCoverageAreasRequest({ id: data?.inspectionFirmId }));
        hideDialog();
      } else if (response?.status === 400 || response?.status === 404) {
        setShowErrorNotification(true);
        setErrorMessage(
          response?.data?.detail || "Failed to save coverage area"
        );
      } else {
        setShowErrorNotification(true);
        setErrorMessage("Failed to save coverage area. Please try again.");
      }
    } catch (error) {
      setShowLoading(false);
      setErrorMessage("Failed to save coverage area. Please try again.");
      setShowErrorNotification(true);
    }
  };

  const hideDialog = () => {
    formik.resetForm();
    closeDialog();
  };

  const handleDeleteCoverageArea = async () => {
    setShowLoading(true);
    try {
      const response = await deleteCoverageAreaRequest(
        data?.inspectionFirmId!,
        {
          id: data?.id,
        }
      );
      setShowLoading(false);
      if (response?.status === 200 || response?.status === 204) {
        dispatch(getCoverageAreasRequest({ id: data?.inspectionFirmId }));
        hideDialog();
      } else if (response?.status === 400) {
        setShowErrorNotification(true);
        setErrorMessage(
          response?.data?.detail || "Failed to delete coverage area"
        );
      }
    } catch (error) {
      setShowLoading(false);
      setErrorMessage("Failed to delete coverage area. Please try again.");
      setShowErrorNotification(true);
    }
  };

  return (
    <DialogContainer
      title={`${
        addCoverageAreaCase ? "" : "Edit "
      }Inspection Firm - Coverage Area`}
      showDialog={showDialog}
      closeDialog={closeDialog}
      onSubmit={formik.handleSubmit}
      leftButtonComponent={
        !addCoverageAreaCase &&
        data && (
          <Button
            sx={{ mr: 2 }}
            startIcon={<DeleteOutlinedIcon color="error" />}
            color="error"
            onClick={handleDeleteCoverageArea}
            disabled={showLoading}
          >
            Delete Coverage Area
          </Button>
        )
      }
    >
      <form onSubmit={formik.handleSubmit}>
        <Box className="user-name-layout">
          <Typography className="label-txt" sx={{ mb: 1 }}>
            Coverage Area Name
          </Typography>
          <TextInput
            sx={{ width: "100% !important" }}
            className="field-input"
            id="coverageAreaName"
            name="coverageAreaName"
            variant="outlined"
            placeholder="Enter coverage area name"
            value={formik.values.coverageAreaName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.coverageAreaName &&
              Boolean(formik.errors.coverageAreaName)
            }
            helperText={
              formik.touched.coverageAreaName && formik.errors.coverageAreaName
            }
          />
        </Box>
        <Box className="user-name-layout">
          <Typography className="label-txt" sx={{ mb: 1 }}>
            Coverage Area
          </Typography>
          <Box sx={{ width: "calc(100% - 180px)" }}>
            <FormControl
              sx={{ width: "200px" }}
              error={formik.touched.country && Boolean(formik.errors.country)}
            >
              <InputLabel id="country-label">Country*</InputLabel>
              <Select
                labelId="country-label"
                id="country"
                name="country"
                label="Country*"
                value={formik.values.country}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
              {formik.touched.country && formik.errors.country && (
                <Typography
                  color="error"
                  variant="caption"
                  sx={{ mt: 0.5, ml: 1.5 }}
                >
                  {formik.errors.country}
                </Typography>
              )}
            </FormControl>
          </Box>
        </Box>
        <Box className="user-name-layout">
          <Typography className="label-txt" sx={{ mb: 1 }}>
            Zip Codes
          </Typography>
          <TextField
            fullWidth
            id="zipCodes"
            name="zipCodes"
            multiline
            rows={8}
            variant="outlined"
            placeholder={
              formik.values.country === "USA"
                ? "Enter 5-digit zip codes...\nExample:\n10001, 10002, 10003\n20001\n30001, 30002"
                : formik.values.country === "Canada"
                ? "Enter Canadian postal codes...\nExample:\nM6L 0A6, M5V 3A8\nK1A 0A6\nV6B 1A1, V6B 1A2"
                : "Enter zip codes...\nExample:\n10001, 10002, 10003\n20001\n30001, 30002"
            }
            value={formik.values.zipCodes}
            onChange={(e) => {
              const value = e.target.value;
              let filteredValue;

              if (formik.values.country === "USA") {
                // USA: Only allow numbers, commas, spaces, and newlines
                filteredValue = value.replace(/[^0-9,\s\n]/g, "");
              } else if (formik.values.country === "Canada") {
                // Canada: Allow letters (A-Z, a-z), numbers, spaces, commas, and newlines
                // Convert to uppercase immediately
                filteredValue = value
                  .replace(/[^A-Za-z0-9,\s\n]/g, "")
                  .toUpperCase();
              } else {
                // Other countries: Allow A-Z, 0-9, commas, spaces, and newlines
                filteredValue = value.replace(/[^A-Z0-9,\s\n]/gi, "");
              }

              formik.setFieldValue("zipCodes", filteredValue.toUpperCase());
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.zipCodes && Boolean(formik.errors.zipCodes)}
            helperText={
              formik.touched.zipCodes && formik.errors.zipCodes
                ? formik.errors.zipCodes
                : formik.values.country === "USA"
                ? "Enter 5-digit zip codes (e.g., 10001, 20002)"
                : formik.values.country === "Canada"
                ? "Enter Canadian postal codes (e.g., M6L 0A6, K1A 0A6)"
                : "Enter one zip code per line or separate with commas"
            }
            sx={{
              "& .MuiOutlinedInput-root": {
                fontFamily: "monospace",
                fontSize: "14px",
              },
            }}
          />
        </Box>
      </form>

      {showLoading && <Loading />}
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          message={errorMessage}
          onClose={() => setShowErrorNotification(false)}
        />
      )}
    </DialogContainer>
  );
}
